// Short, operator-facing reason a rate card can never be weighed. The same
// three defects shellWeighability (palletCompute.ts) refuses on, in the same
// order, but as one fragment for the "Not available" list under the pack chips.
import { missingTareLinesOf, type TareComponent } from '@/lib/palletCompute'

export interface UnweighableReason { reason: string; missingLines: string[] }

/** null when the card is weighable. Order mirrors shellWeighability. */
export function unweighableReason(components: TareComponent[], standard: number): UnweighableReason | null {
  if (components.length === 0) return { reason: 'the card has no cost lines', missingLines: [] }
  if (!standard || standard <= 0) return { reason: 'the card has no units per pallet', missingLines: [] }
  const missing = missingTareLinesOf(components)
  if (missing.length > 0) {
    return {
      reason: `${missing.length === 1 ? 'a cost line has' : 'cost lines have'} no tare (${missing.join(', ')})`,
      missingLines: missing,
    }
  }
  return null
}

export interface WeighCandidate {
  product_id:       string
  product_code:     string
  fruit_type:       string | null
  units_per_pallet: number
  components:       TareComponent[]
}

export interface UnweighablePack { reason: string; productId: string; missingLines: string[] }

/**
 * Which offered pack codes can NEVER be weighed on this run. A code is
 * unweighable only if it has >= 1 candidate that passes `fold` (the run's
 * fruit match) and EVERY such candidate fails `weighability` (a reason, or null
 * when the card is weighable). One weighable variant keeps the code open; a code
 * with no matching candidate is not in the map (the chips' no-rate-card check
 * owns that case). Fail-open on an unread catalogue is the caller's job.
 */
export function unweighableByCode(
  candidates: readonly WeighCandidate[],
  fold: (c: WeighCandidate) => boolean,
  weighability: (c: WeighCandidate) => UnweighableReason | null,
): Map<string, UnweighablePack> {
  const byCode = new Map<string, WeighCandidate[]>()
  for (const c of candidates) {
    if (!fold(c)) continue
    byCode.set(c.product_code, [...(byCode.get(c.product_code) ?? []), c])
  }
  const out = new Map<string, UnweighablePack>()
  for (const [code, list] of byCode) {
    const fails = list.map(c => ({ c, why: weighability(c) }))
    if (fails.some(f => f.why === null)) continue
    const first = fails[0]
    out.set(code, { reason: first.why!.reason, productId: first.c.product_id, missingLines: first.why!.missingLines })
  }
  return out
}
